/**
 * figure — 把 OCR 從考卷讀到的「圖表資料」在 App 端**重新生成**一張乾淨的圖
 * (統計表 / 長條圖 / 象形圖 / 時鐘),取代「裁照片」。
 *
 * 緣由(boss 2026-06-10「圖表你能自己生成嗎 我覺得應該很容易」):與其從側躺、糊掉
 * 的手機照片裁出統計表,不如讓 OCR 讀出表格/圖的**數字**,App 用乾淨向量/表格畫出
 * 來。清楚、無版權、可在錯題本/複習重現。
 *
 * 本檔:Figure 型別 + `normalizeFigure`(把模型回的雜資料收斂成合法 Figure,壞的回
 * null)+ `renderFigure`(畫成學術風 DOM)。純資料/DOM,無網路。
 *
 * 視覺 = 學術風格(--ef-* token):墨線、學術綠重點、紙感、serif 標題。
 */

/** 統計表:表頭 + 多列。 */
export interface TableFigure {
  type: 'table';
  title?: string;
  headers: string[];
  rows: (string | number)[][];
}

/** 長條圖:類別 + 數值。 */
export interface BarFigure {
  type: 'bar';
  title?: string;
  labels: string[];
  values: number[];
  unit?: string;
}

/** 象形圖(一個符號代表 N 個):類別 + 數量。 */
export interface PictographFigure {
  type: 'pictograph';
  title?: string;
  labels: string[];
  counts: number[];
  /** 代表符號(emoji);預設 ⭐。 */
  symbol?: string;
  /** 一個符號代表幾個(預設 1)。 */
  per?: number;
}

/** 時鐘:時 + 分。 */
export interface ClockFigure {
  type: 'clock';
  hour: number;
  minute: number;
}

export type Figure = TableFigure | BarFigure | PictographFigure | ClockFigure;

// ── 正規化(模型輸出很雜,逐型收斂;不合法回 null,絕不丟例外)──

function toStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/[^0-9.\-]/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map(toStr) : [];
}

function numArray(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => toNum(x) ?? 0);
}

/**
 * 把模型回的任意物件收斂成合法 Figure;讀不出有意義內容回 null
 * (呼叫端就當「這題沒有可生成的圖」)。
 */
export function normalizeFigure(raw: unknown): Figure | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const type = toStr(o.type).toLowerCase();

  if (type === 'table') {
    const headers = strArray(o.headers);
    const rawRows = Array.isArray(o.rows) ? o.rows : [];
    const rows = rawRows
      .map((r) => (Array.isArray(r) ? r.map((c) => toStr(c)) : []))
      .filter((r) => r.length > 0);
    if (rows.length === 0 && headers.length === 0) return null;
    return { type: 'table', title: toStr(o.title) || undefined, headers, rows };
  }

  if (type === 'bar') {
    const labels = strArray(o.labels);
    const values = numArray(o.values);
    if (labels.length === 0 || values.length === 0) return null;
    return { type: 'bar', title: toStr(o.title) || undefined, labels, values, unit: toStr(o.unit) || undefined };
  }

  if (type === 'pictograph') {
    const labels = strArray(o.labels);
    const counts = numArray(o.counts);
    if (labels.length === 0 || counts.length === 0) return null;
    const per = toNum(o.per);
    return {
      type: 'pictograph',
      title: toStr(o.title) || undefined,
      labels,
      counts,
      symbol: toStr(o.symbol) || undefined,
      per: per && per > 0 ? per : undefined,
    };
  }

  if (type === 'clock') {
    const hour = toNum(o.hour);
    const minute = toNum(o.minute);
    if (hour === null || minute === null) return null;
    return { type: 'clock', hour: ((hour % 12) + 12) % 12 || 12, minute: ((minute % 60) + 60) % 60 };
  }

  return null;
}

// ── 渲染(學術風 DOM)──

const FIGURE_STYLE_ID = 'ef-figure-style';
function ensureFigureStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FIGURE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = FIGURE_STYLE_ID;
  style.textContent = `
.ef-figure { margin:8px 0; padding:12px 14px; background:var(--ef-paper-bright); border:1px solid var(--ef-line); border-radius:var(--ef-radius); }
.ef-figure__title { font-family:var(--ef-serif); font-weight:700; font-size:14px; color:var(--ef-ink); margin-bottom:8px; }
.ef-figure__tag { font-size:11px; color:var(--ef-green-dark); font-weight:700; letter-spacing:0.5px; margin-bottom:6px; }
.ef-figure table { border-collapse:collapse; width:100%; font-size:14px; color:var(--ef-ink); }
.ef-figure th, .ef-figure td { border:1px solid var(--ef-line); padding:6px 10px; text-align:center; }
.ef-figure th { background:rgba(31,111,92,0.08); font-weight:700; color:var(--ef-green-dark); }
.ef-figure__bar-row { display:flex; align-items:center; gap:8px; margin:4px 0; font-size:13px; color:var(--ef-ink); }
.ef-figure__bar-label { min-width:64px; text-align:right; color:var(--ef-ink-soft); }
.ef-figure__bar-track { flex:1; background:var(--ef-paper); border:1px solid var(--ef-line); border-radius:4px; overflow:hidden; }
.ef-figure__bar-fill { height:18px; background:var(--ef-green); }
.ef-figure__bar-val { min-width:36px; color:var(--ef-ink-soft); }
.ef-figure__picto-row { display:flex; align-items:center; gap:8px; margin:3px 0; font-size:18px; line-height:1.2; }
.ef-figure__picto-label { min-width:64px; text-align:right; font-size:13px; color:var(--ef-ink-soft); }
.ef-figure__picto-count { font-size:12px; color:var(--ef-sub); margin-left:4px; }
`;
  document.head.appendChild(style);
}

/** 把一份 Figure 畫成學術風 DOM(呼叫端自行 append)。 */
export function renderFigure(fig: Figure): HTMLElement {
  ensureFigureStyle();
  const wrap = document.createElement('div');
  wrap.className = 'ef-figure';
  wrap.setAttribute('data-figure-type', fig.type);

  const tag = document.createElement('div');
  tag.className = 'ef-figure__tag';
  tag.textContent = '📊 系統重繪的參考圖';
  wrap.appendChild(tag);

  const title = (fig as { title?: string }).title;
  if (title) {
    const t = document.createElement('div');
    t.className = 'ef-figure__title';
    t.textContent = title;
    wrap.appendChild(t);
  }

  if (fig.type === 'table') wrap.appendChild(renderTable(fig));
  else if (fig.type === 'bar') wrap.appendChild(renderBar(fig));
  else if (fig.type === 'pictograph') wrap.appendChild(renderPictograph(fig));
  else wrap.appendChild(renderClock(fig));

  return wrap;
}

function renderTable(fig: TableFigure): HTMLElement {
  const table = document.createElement('table');
  if (fig.headers.length > 0) {
    const thead = document.createElement('thead');
    const tr = document.createElement('tr');
    for (const h of fig.headers) {
      const th = document.createElement('th');
      th.textContent = h;
      tr.appendChild(th);
    }
    thead.appendChild(tr);
    table.appendChild(thead);
  }
  const tbody = document.createElement('tbody');
  for (const row of fig.rows) {
    const tr = document.createElement('tr');
    for (const cell of row) {
      const td = document.createElement('td');
      td.textContent = String(cell);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  return table;
}

function renderBar(fig: BarFigure): HTMLElement {
  const box = document.createElement('div');
  const max = Math.max(1, ...fig.values.map((v) => Math.abs(v)));
  fig.labels.forEach((label, i) => {
    const val = fig.values[i] ?? 0;
    const row = document.createElement('div');
    row.className = 'ef-figure__bar-row';
    const lab = document.createElement('span');
    lab.className = 'ef-figure__bar-label';
    lab.textContent = label;
    const track = document.createElement('span');
    track.className = 'ef-figure__bar-track';
    const fill = document.createElement('span');
    fill.className = 'ef-figure__bar-fill';
    fill.style.display = 'block';
    fill.style.width = `${Math.round((Math.abs(val) / max) * 100)}%`;
    track.appendChild(fill);
    const valEl = document.createElement('span');
    valEl.className = 'ef-figure__bar-val';
    valEl.textContent = String(val) + (fig.unit ?? '');
    row.append(lab, track, valEl);
    box.appendChild(row);
  });
  return box;
}

function renderPictograph(fig: PictographFigure): HTMLElement {
  const box = document.createElement('div');
  const symbol = fig.symbol || '⭐';
  const per = fig.per && fig.per > 0 ? fig.per : 1;
  fig.labels.forEach((label, i) => {
    const count = fig.counts[i] ?? 0;
    const symbols = Math.max(0, Math.round(count / per));
    const row = document.createElement('div');
    row.className = 'ef-figure__picto-row';
    const lab = document.createElement('span');
    lab.className = 'ef-figure__picto-label';
    lab.textContent = label;
    const syms = document.createElement('span');
    syms.textContent = symbol.repeat(symbols);
    const cnt = document.createElement('span');
    cnt.className = 'ef-figure__picto-count';
    cnt.textContent = `(${count})`;
    row.append(lab, syms, cnt);
    box.appendChild(row);
  });
  if (per > 1) {
    const legend = document.createElement('div');
    legend.className = 'ef-figure__picto-count';
    legend.textContent = `每個 ${symbol} 代表 ${per} 個`;
    box.appendChild(legend);
  }
  return box;
}

function renderClock(fig: ClockFigure): SVGSVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('width', '120');
  svg.setAttribute('height', '120');

  const circle = document.createElementNS(NS, 'circle');
  circle.setAttribute('cx', '50');
  circle.setAttribute('cy', '50');
  circle.setAttribute('r', '46');
  circle.setAttribute('fill', '#fff');
  circle.setAttribute('stroke', '#1a1a1a');
  circle.setAttribute('stroke-width', '2');
  svg.appendChild(circle);

  // 12 個刻度
  for (let h = 0; h < 12; h++) {
    const a = (h / 12) * Math.PI * 2;
    const x1 = 50 + Math.sin(a) * 40;
    const y1 = 50 - Math.cos(a) * 40;
    const x2 = 50 + Math.sin(a) * 44;
    const y2 = 50 - Math.cos(a) * 44;
    const tick = document.createElementNS(NS, 'line');
    tick.setAttribute('x1', String(x1));
    tick.setAttribute('y1', String(y1));
    tick.setAttribute('x2', String(x2));
    tick.setAttribute('y2', String(y2));
    tick.setAttribute('stroke', '#1a1a1a');
    tick.setAttribute('stroke-width', '1.5');
    svg.appendChild(tick);
  }

  const hourAngle = ((fig.hour % 12) + fig.minute / 60) / 12 * Math.PI * 2;
  const minAngle = (fig.minute / 60) * Math.PI * 2;
  const hand = (len: number, angle: number, w: string, color: string): SVGLineElement => {
    const line = document.createElementNS(NS, 'line');
    line.setAttribute('x1', '50');
    line.setAttribute('y1', '50');
    line.setAttribute('x2', String(50 + Math.sin(angle) * len));
    line.setAttribute('y2', String(50 - Math.cos(angle) * len));
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', w);
    line.setAttribute('stroke-linecap', 'round');
    return line;
  };
  svg.appendChild(hand(24, hourAngle, '3.5', '#1a1a1a'));
  svg.appendChild(hand(34, minAngle, '2.5', '#1f6f5c'));

  return svg;
}
